import * as R from 'ramda'
import React, { useState } from 'react'
import styled from 'styled-components'
import tw from 'twin.macro'
import { Accordion, Button } from 'semantic-ui-react'

import useAxios, { mutate } from 'hooks/useAxios'
import IntercomAuthentication from 'components/IntercomAuthentication'
import { Loader } from 'shared/components/Loader'
import SurveyPublishDescription from 'components/SurveyPublishDescription'
import { AccordionAutoIndex } from 'components/AccordionAutoIndex'
import { IntegrationAccordionTitleContent } from 'components/IntegrationAccordionTitleContent'
import { toast } from 'libs/toast'
import useSaveUserActivityLog, { ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import PullTicketIntegration from './PullTicketIntegration'
import UpdateConversationAndPrivateNoteIntegration from './UpdateConversationAndPrivateNoteIntegration'
import UpdateTagIntegration from './UpdateTagIntegration'
import UpdateTicketCustomFieldIntegration from './UpdateTicketCustomFieldIntegration'

/**
 *
 * @param {object} props
 * @param {object} props.intercomSurveyConfiguration
 * @param {string} props.surveyToken
 * @param {number} props.surveyId
 */
export default function IntercomInAppPublish({
  intercomSurveyConfiguration,
  surveyToken,
  surveyId,
}) {
  const { data: credential, error } = useAxios({
    url: 'intercom/authentication/',
  })

  const blankIndex = -1
  const [activeIndex, setActiveIndex] = useState(blankIndex)
  const saveUserActivityLog = useSaveUserActivityLog()

  const { data: surveyIntegrationSettings } = useAxios({
    url: `survey/${surveyToken}/survey-integration-setting/?integration_type=intercom`,
  })

  const { fetch: updateSurveyIntegrationSetting, loading: updating } = useAxios({
    method: 'patch',
    skip: true,
  })

  const credentialLoading = !credential && !R.path(['response', 'data'], error)

  if (credentialLoading || !surveyIntegrationSettings) {
    return <Loader withContainer />
  }

  function handleClickAccordion(_, { index }) {
    setActiveIndex((prevActiveIndex) => (prevActiveIndex === index ? blankIndex : index))
  }

  function getResults(integrationSetting, responseResult) {
    return integrationSetting.results.map((setting) => {
      if (setting.id === responseResult.id) {
        return responseResult
      } else {
        return setting
      }
    })
  }

  function handleIntegrationSettingChange(newSetting) {
    const data = {
      name: newSetting.name,
      integration_type: 'intercom',
      option_name: 'is_active',
      option_value: newSetting.isActive,
    }

    const action = ACTION_NAME.connected_intercom
    const logRequestBody = { integration: newSetting.name, enabled: newSetting.isActive }
    updateSurveyIntegrationSetting({
      url: `/survey/survey-integration-setting/by-survey/${surveyToken}/additional-option/`,
      data,
    })
      .then((response) => {
        mutate(
          `survey/${surveyToken}/survey-integration-setting/?integration_type=intercom`,
          (integrationSetting) => ({
            ...integrationSetting,
            results: getResults(integrationSetting, response.data),
          }),
          true
        )

        if (R.prop('is_active', response.data.additional_option)) {
          toast.success('Integration activated!')
        } else {
          toast.success('Integration deactivated.')
        }

        saveUserActivityLog({
          action,
          target: `sur_${surveyId}`,
          statusCode: response.status,
          requestBody: logRequestBody,
        })
      })
      .catch((error) => {
        toast.error(
          'Oops! Something went wrong. Please try again or contact Simplesat support for help.'
        )
        saveUserActivityLog({
          action,
          target: `sur_${surveyId}`,
          requestBody: logRequestBody,
          errorResponseBody: logRequestBody,
          statusCode: error.response.status,
        })
      })
  }

  const surveyIntegrationSettingMapper = surveyIntegrationSettings.results.reduce(
    (surveyIntegrationSettingMapper, surveyIntegrationSetting) => ({
      ...surveyIntegrationSettingMapper,
      [surveyIntegrationSetting.name]: surveyIntegrationSetting,
    }),
    {}
  )

  const pullTicketSetting = surveyIntegrationSettingMapper.pull_ticket
  const pushTicketNoteSetting = surveyIntegrationSettingMapper.push_ticket_note
  const pushTagSetting = surveyIntegrationSettingMapper.push_tag
  const pushTicketCustomFieldSetting = surveyIntegrationSettingMapper.push_ticket_custom_field

  const isAuthenticated = Boolean(credential)
  function getIsActiveStatus(isAuthenticated, integrationSetting) {
    if (isAuthenticated && integrationSetting) {
      return R.prop('is_active', integrationSetting.additional_option)
    } else {
      return false
    }
  }

  return (
    <React.Fragment>
      <Container>
        <div>
          <SurveyPublishDescription
            content={intercomSurveyConfiguration.sendAsAppOverview.content}
            linkText={intercomSurveyConfiguration.sendAsAppOverview.linkText}
            linkUrl={intercomSurveyConfiguration.sendAsAppOverview.linkUrl}
          />

          <Button
            as="a"
            target="_blank"
            rel="noreferrer"
            href="https://app.intercom.com/"
            className="!mt-3"
          >
            <i className="far fa-external-link mr-2" />
            Launch Intercom to configure
          </Button>
        </div>
        <div className="ui images">
          <img
            src="https://cdn.simplesat.io/intercom/send-as-an-app.png"
            alt="Send as an app"
            className="ui image float-right"
          />
        </div>
      </Container>
      <div className="mt4">
        <IntercomAuthentication isAuthenticated={isAuthenticated} />
      </div>

      <hr />
      <h3>Configure integrations</h3>

      <Accordion fluid styled>
        <AccordionAutoIndex activeIndex={activeIndex}>
          <AccordionAutoIndex.Item>
            <Accordion.Title onClick={handleClickAccordion}>
              <IntegrationAccordionTitleContent
                title="Pull conversation details"
                active={getIsActiveStatus(isAuthenticated, pullTicketSetting)}
                isAutomaticActive={false}
              />
            </Accordion.Title>
            <Accordion.Content>
              <PullTicketIntegration
                onChange={handleIntegrationSettingChange}
                loading={updating}
                isAuthenticated={isAuthenticated}
                isIntegrationActive={getIsActiveStatus(isAuthenticated, pullTicketSetting)}
              />
            </Accordion.Content>
          </AccordionAutoIndex.Item>
          <AccordionAutoIndex.Item>
            <Accordion.Title onClick={handleClickAccordion}>
              <IntegrationAccordionTitleContent
                title="Add notes to rated conversations"
                active={getIsActiveStatus(isAuthenticated, pushTicketNoteSetting)}
                isAutomaticActive={false}
              />
            </Accordion.Title>
            <Accordion.Content>
              <UpdateConversationAndPrivateNoteIntegration
                onChange={handleIntegrationSettingChange}
                loading={updating}
                isAuthenticated={isAuthenticated}
                isIntegrationActive={getIsActiveStatus(isAuthenticated, pushTicketNoteSetting)}
              />
            </Accordion.Content>
          </AccordionAutoIndex.Item>
          <AccordionAutoIndex.Item>
            <Accordion.Title onClick={handleClickAccordion}>
              <IntegrationAccordionTitleContent
                title="Add tags to rated conversations"
                active={getIsActiveStatus(isAuthenticated, pushTagSetting)}
                isAutomaticActive={false}
              />
            </Accordion.Title>
            <Accordion.Content>
              <UpdateTagIntegration
                onChange={handleIntegrationSettingChange}
                loading={updating}
                isAuthenticated={isAuthenticated}
                isIntegrationActive={getIsActiveStatus(isAuthenticated, pushTagSetting)}
              />
            </Accordion.Content>
          </AccordionAutoIndex.Item>
          <AccordionAutoIndex.Item>
            <Accordion.Title onClick={handleClickAccordion}>
              <IntegrationAccordionTitleContent
                title="Update conversation custom attributes with rating details"
                shouldHideActiveLabel
              />
            </Accordion.Title>
            <Accordion.Content>
              <UpdateTicketCustomFieldIntegration
                integrationSetting={pushTicketCustomFieldSetting}
                surveyToken={surveyToken}
                surveyId={surveyId}
                isAuthenticated={isAuthenticated}
              />
            </Accordion.Content>
          </AccordionAutoIndex.Item>
        </AccordionAutoIndex>
      </Accordion>
    </React.Fragment>
  )
}

const Container = styled.div`
  ${tw`flex flex-wrap md:flex-nowrap`}

  .images {
    ${tw`flex-shrink md:mt-0 mt-5`}
  }
`



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/IntercomInAppPublish/index.js